import { getRandomColor } from '@utils/colors';
import { resource } from '@utils/network/network';
import {
    resourcesAtom,
    AgendaResources,
    AgendaResource,
} from '@states/resources';
import { getRecoilExternalState } from '@utils/recoil/RecoilExternalStatePortal';
import { RESOURCE_TYPES } from '@utils/constants';
import type { ResourceResponse } from '@kiute-tech/network-kiute-pro';

export const getPhysicalResourceCombinedId = (
    id?: number,
    prUnitId?: number,
): number => {
    if (!id || !prUnitId) {
        return -1;
    }
    return id + prUnitId;
};

export const transformResources = (
    rawResources: ResourceResponse,
): AgendaResources =>
    rawResources.reduce((acc, r) => {
        const agendaResource = {
            id: r.id,
            name: r.label || r.firstName,
            color:
                getRecoilExternalState?.(resourcesAtom)?.getValue()?.[r.id]
                    ?.color || getRandomColor(),
            resourceData: r,
        };
        return { ...acc, [r.id]: agendaResource };
    }, {} as AgendaResources);

export const clonePhysicalResources = (
    physResources: ResourceResponse,
): AgendaResources => {
    const clonedFlat = physResources
        .map(({ id, ...physResource }) =>
            Array(physResource.number)
                .fill(null)
                .map((_, i) => {
                    const unitIdIndex = i + 1;
                    const combinedId = getPhysicalResourceCombinedId(
                        id,
                        unitIdIndex,
                    );
                    return {
                        id: combinedId,
                        name: `${physResource.label} ${unitIdIndex}`,
                        color:
                            getRecoilExternalState?.(
                                resourcesAtom,
                            )?.getValue()?.[combinedId]?.color ||
                            getRandomColor(),
                        resourceData: {
                            id,
                            prUnitId: unitIdIndex,
                            ...physResource,
                        },
                    };
                }),
        )
        .flat();

    return clonedFlat.reduce(
        (acc, clone) => ({
            ...acc,
            [clone.id]: clone,
        }),
        {} as AgendaResources,
    );
};

export const getResources = async (): Promise<AgendaResources> => {
    const humanResourcesReq = resource?.fetchHumanResources({
        onlyPlanningActive: true,
    });
    const physicalResourcesReq = resource?.fetchPhysicalResources({
        onlyPlanningActive: true,
    });

    const humanRes = await humanResourcesReq;
    const physicalRes = await physicalResourcesReq;

    if (
        humanRes &&
        physicalRes &&
        ('length' in humanRes || 'length' in physicalRes)
    ) {
        return {
            ...('length' in humanRes ? transformResources(humanRes) : {}),
            ...('length' in physicalRes
                ? clonePhysicalResources(physicalRes)
                : {}),
        };
    }

    return Promise.reject();
};

export const sortResourcesByOrder = (
    ressources: AgendaResource[],
): AgendaResource[] =>
    [...ressources].sort(
        (r1, r2) => r1.resourceData.order - r2.resourceData.order,
    );

export const getFirstValid = (
    resources: AgendaResource[],
    checkFn: (r1: AgendaResource | undefined, r2: AgendaResource) => boolean,
): AgendaResource | undefined =>
    resources.reduce(
        (acc, r) => (checkFn(acc, r) ? r : acc),
        undefined as AgendaResource | undefined,
    );

export const firstSelectableResource = (
    resources: AgendaResources,
): AgendaResource | undefined => {
    const sortedResources = sortResourcesByOrder(Object.values(resources));
    const human = getFirstValid(
        sortedResources,
        (acc, r) => r?.resourceData.objectType === RESOURCE_TYPES.HUMAN && !acc,
    );

    return (
        human ??
        getFirstValid(
            sortedResources,
            (acc, r) =>
                r?.resourceData.objectType === RESOURCE_TYPES.PHYSICAL && !acc,
        )
    );
};
